import React from "react";
import { CheckCircle, XCircle, Clock } from "lucide-react";

interface TimeSlot {
  id: string;
  period: string;
  startTime: string;
  endTime: string;
}

interface WeeklyAvailabilityGridProps<T = any> {
  days: string[];
  timeSlots: TimeSlot[];
  availability: T[];
  selectedEntityId: string | null;
  getEntityId: (item: T) => string;
  getIsAvailable: (item: T) => boolean;
  getDay: (item: T) => string;
  getTimeSlotId: (item: T) => string;
  onToggle: (day: string, timeSlotId: string) => void;
  getPartialAvailability?: (day: string, timeSlotId: string) => boolean;
  onCellClick?: (day: string, timeSlotId: string) => void;
  disabled?: boolean;
  showLegend?: boolean;
  className?: string;
  readOnly?: boolean;
  /** Daha sade üst şerit, slate tablo çerçevesi ve yumuşak hücre renkleri (provider müsaitlik vb.). */
  refined?: boolean;
}

const WeeklyAvailabilityGrid: React.FC<WeeklyAvailabilityGridProps> = ({
  days,
  timeSlots,
  availability,
  selectedEntityId,
  getEntityId,
  getIsAvailable,
  getDay,
  getTimeSlotId,
  onToggle,
  getPartialAvailability,
  onCellClick,
  disabled = false,
  showLegend = true,
  className = "",
  readOnly = false,
  refined = false,
}) => {
  const checkAvailability = (day: string, timeSlotId: string): boolean => {
    if (!selectedEntityId) return false;

    const item = availability.find((a) => getEntityId(a) === selectedEntityId && getDay(a) === day && getTimeSlotId(a) === timeSlotId);
    return item ? getIsAvailable(item) : false;
  };

  const colTemplate = refined
    ? `minmax(7.75rem, 9.25rem) repeat(${days.length}, minmax(3.25rem, 1fr))`
    : `120px repeat(${days.length}, 1fr)`;

  const headerRowCls = refined ? "grid border-b border-slate-200/90 bg-slate-100" : "grid bg-gray-50";
  const cornerCls = refined
    ? "sticky top-0 left-0 z-30 border-b border-r border-slate-200/90 bg-slate-100 px-3 py-3 text-[10px] font-bold uppercase tracking-[0.14em] text-slate-500 sm:text-xs sm:tracking-[0.12em] shadow-[1px_1px_0_0_rgba(15,23,42,0.04)]"
    : "p-2 sm:p-3 text-xs sm:text-sm font-medium text-gray-700 border-r border-gray-200";
  const dayHeaderCls = refined
    ? "sticky top-0 z-20 border-b border-r border-slate-200/80 bg-slate-100/95 px-2 py-3 text-center text-[10px] font-bold uppercase tracking-widest text-slate-600 backdrop-blur-sm last:border-r-0 sm:text-[11px] sm:tracking-[0.18em]"
    : "p-2 sm:p-3 text-xs sm:text-sm font-medium text-gray-700 text-center border-r border-gray-200 last:border-r-0";
  const timeLabelCls = refined
    ? "sticky left-0 z-10 border-b border-r border-slate-200/75 bg-slate-50 px-3 py-2.5 shadow-[4px_0_14px_-6px_rgba(15,23,42,0.12)]"
    : "p-2 sm:p-3 bg-gray-50 border-r border-gray-200";
  const rowCls = refined ? "grid border-b border-slate-100 last:border-b-0" : "grid border-b border-gray-200 last:border-b-0";
  const cellPadCls = refined
    ? "border-b border-r border-slate-100/90 bg-white p-1.5 last:border-r-0 sm:p-2"
    : "p-1 sm:p-2 border-r border-gray-200 last:border-r-0";

  const cellButtonCls = (displayState: string, isDisabled: boolean) => {
    const focusRing = refined ? "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-400/90 focus-visible:ring-offset-1" : "";
    const base = `w-full min-h-[2.5rem] rounded-md border flex items-center justify-center transition-[box-shadow,background-color,border-color,filter] duration-150 sm:min-h-[2.75rem] ${focusRing} ${
      isDisabled ? "opacity-50 cursor-not-allowed" : refined ? "hover:brightness-[0.98] active:brightness-[0.96]" : "hover:scale-105 active:scale-95"
    }`;
    if (refined) {
      if (displayState === "available") {
        return `${base} border-emerald-200/90 bg-gradient-to-b from-emerald-50 to-emerald-50/80 text-emerald-900 shadow-[inset_0_1px_0_0_rgba(255,255,255,0.65)]`;
      }
      if (displayState === "partial") {
        return `${base} border-amber-200/90 bg-gradient-to-b from-amber-50 to-amber-50/75 text-amber-950 shadow-[inset_0_1px_0_0_rgba(255,255,255,0.55)]`;
      }
      if (readOnly) {
        return `${base} border-slate-200/90 bg-slate-100/90 text-slate-500`;
      }
      return `${base} border-rose-200/85 bg-gradient-to-b from-rose-50 to-rose-50/75 text-rose-900 shadow-[inset_0_1px_0_0_rgba(255,255,255,0.55)]`;
    }
    if (displayState === "available") {
      return `${base} border-2 bg-green-50 border-green-200 text-green-700 hover:bg-green-100 shadow-sm`;
    }
    if (displayState === "partial") {
      return `${base} border-2 bg-yellow-100 border-yellow-300 text-yellow-700 hover:bg-yellow-200 shadow-sm`;
    }
    if (readOnly) {
      return `${base} border-2 bg-gray-100 border-gray-300 text-gray-500`;
    }
    return `${base} border-2 bg-red-100 border-red-300 text-red-700 hover:bg-red-200 shadow-sm`;
  };

  const iconCls = refined ? "h-4 w-4 sm:h-[1.125rem] sm:w-[1.125rem]" : "drop-shadow-sm sm:h-5 sm:w-5";

  const legendRefined = showLegend && (
    <div className="flex flex-col gap-2 border-b border-slate-200/90 bg-slate-50/50 px-3 py-3 sm:flex-row sm:items-center sm:justify-between sm:gap-4">
      <span className="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-600">Durumlar</span>
      <div className="flex flex-wrap gap-2">
        <span className="inline-flex items-center gap-1.5 rounded-full border border-emerald-200 bg-emerald-50/80 px-2.5 py-1 text-xs font-medium text-emerald-900">
          <CheckCircle size={14} className="shrink-0 text-emerald-600" />
          Tam müsait
        </span>
        <span className="inline-flex items-center gap-1.5 rounded-full border border-amber-200 bg-amber-50/80 px-2.5 py-1 text-xs font-medium text-amber-900">
          <Clock size={14} className="shrink-0 text-amber-600" />
          Kısmi müsait
        </span>
        <span className="inline-flex items-center gap-1.5 rounded-full border border-rose-200 bg-rose-50/80 px-2.5 py-1 text-xs font-medium text-rose-900">
          <XCircle size={14} className="shrink-0 text-rose-600" />
          Müsait değil
        </span>
      </div>
    </div>
  );

  const tableGrid = (
    <>
      <div className={headerRowCls} style={{ gridTemplateColumns: colTemplate }}>
        <div className={cornerCls}>Saat / Gün</div>
        {days.map((day) => (
          <div key={day} className={dayHeaderCls}>
            <span className="hidden sm:inline">{day}</span>
            <span className="sm:hidden">{day.substring(0, 3)}</span>
          </div>
        ))}
      </div>

      {timeSlots.map((slot) => (
        <div key={slot.id} className={rowCls} style={{ gridTemplateColumns: colTemplate }}>
          <div className={timeLabelCls}>
            <div className={`text-xs sm:text-sm font-semibold ${refined ? "text-slate-900" : "text-gray-900"}`}>
              <span className="hidden sm:inline">{slot.period}</span>
              <span className="sm:hidden">{slot.period.replace(". Saat", "")}</span>
            </div>
            <div className={`mt-0.5 text-[10px] tabular-nums sm:text-xs ${refined ? "text-slate-500" : "text-gray-500"}`}>
              {slot.startTime}
              {refined ? " – " : " - "}
              {slot.endTime}
            </div>
          </div>

          {days.map((day) => {
            const isAvailable = checkAvailability(day, slot.id);
            const isPartial = getPartialAvailability ? getPartialAvailability(day, slot.id) : false;
            const isDisabled = disabled || !selectedEntityId || (readOnly && !(isAvailable || isPartial));

            const displayState = isPartial ? "partial" : isAvailable ? "available" : "unavailable";

            return (
              <div key={`${day}-${slot.id}`} className={cellPadCls}>
                <button
                  type="button"
                  onClick={() => {
                    if (isDisabled) return;
                    if (onCellClick) {
                      onCellClick(day, slot.id);
                    } else {
                      onToggle(day, slot.id);
                    }
                  }}
                  disabled={isDisabled}
                  className={cellButtonCls(displayState, isDisabled)}
                  title={
                    !selectedEntityId
                      ? "Önce bir seçim yapın"
                      : disabled
                        ? "Düzenleme devre dışı"
                        : readOnly && !isAvailable
                          ? "Bu saat müsait değil"
                          : displayState === "available"
                            ? readOnly
                              ? "✅ Tamamen müsait - Randevu almak için tıklayın"
                              : "✅ Tamamen müsait - Detaylar için tıklayın"
                            : displayState === "partial"
                              ? "⚠️ Kısmi müsait - Detaylar için tıklayın"
                              : "❌ Müsait Değil - Detaylar için tıklayın"
                  }>
                  {displayState === "available" ? (
                    <CheckCircle className={iconCls} />
                  ) : displayState === "partial" ? (
                    <Clock className={iconCls} />
                  ) : readOnly ? (
                    <XCircle className={`${iconCls} opacity-50`} />
                  ) : (
                    <XCircle className={iconCls} />
                  )}
                </button>
              </div>
            );
          })}
        </div>
      ))}
    </>
  );

  return (
    <div
      className={`${refined ? "mx-2 sm:mx-4 md:mx-5" : "mx-2 space-y-4 sm:mx-4 md:mx-5"} ${className}`}>
      {refined ? (
        <div className="overflow-hidden rounded-xl border border-slate-200/95 bg-slate-50/40 shadow-[0_1px_3px_rgba(15,23,42,0.06)]">
          {legendRefined}
          <div
            className="max-h-[min(75vh,820px)] overflow-auto"
            style={{ WebkitOverflowScrolling: "touch" }}>
            <div className="min-w-[720px]">{tableGrid}</div>
          </div>
        </div>
      ) : (
        <>
          {showLegend && (
            <div className="mb-3 p-2 sm:p-3 bg-blue-50 border border-blue-200 rounded-lg">
              <div className="flex items-center flex-wrap gap-1 sm:gap-2 text-blue-700 text-xs sm:text-sm">
                <div className="flex items-center space-x-1">
                  <CheckCircle size={14} className="text-green-700 sm:w-4 sm:h-4" />
                  <span>Tamamen Müsait</span>
                </div>
                <span className="text-blue-400 hidden sm:inline">|</span>
                <div className="flex items-center space-x-1">
                  <Clock size={14} className="text-yellow-600 sm:w-4 sm:h-4" />
                  <span>Kısmi Müsait</span>
                </div>
                <span className="text-blue-400 hidden sm:inline">|</span>
                <div className="flex items-center space-x-1">
                  <XCircle size={14} className="text-red-600 sm:w-4 sm:h-4" />
                  <span>Müsait Değil</span>
                </div>
              </div>
            </div>
          )}

          <div className="overflow-x-auto rounded-lg border border-gray-200 bg-white">
            <div className="min-w-[650px]">{tableGrid}</div>
          </div>
        </>
      )}
    </div>
  );
};

export default WeeklyAvailabilityGrid;
